import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Search, FileText, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import api, { searchService, transactionService } from '../services/api';
import { toast } from 'sonner';
import { useConfirmDialog } from '../components/DialogHost';

interface SearchHistoryItem {
  id: string;
  search_type: string;
  search_query: string;
  chain?: string;
  results_count: number;
  found_entity_id?: string;
  auto_case_created: boolean;
  auto_case_id?: string;
  metadata?: {
    risk_score?: number;
    risk_level?: string;
    entity_name?: string;
  };
  searched_at: string;
}

export function SearchHistory() {
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [searches, setSearches] = useState<SearchHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    loadSearchHistory();
  }, [filter]);

  const loadSearchHistory = async () => {
    try {
      const data = await searchService.getHistory(20, filter);
      setSearches(data);
    } catch (error) {
      console.error('Failed to load search history:', error);
      toast.error('Failed to load search history');
    } finally {
      setLoading(false);
    }
  };


  const deleteSearch = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const shouldDelete = await confirm({
      title: 'Delete this search?',
      message: 'This will remove it from your history.',
      confirmText: 'Delete',
      danger: true,
    });
    if (!shouldDelete) return;
    try {
      await api.delete(`/search/history/${id}`);
      setSearches(searches.filter(s => s.id !== id));
      toast.success('Search deleted');
    } catch (error) {
      console.error('Failed to delete search:', error);
      toast.error('Failed to delete search');
    }
  };

  const clearAllHistory = async () => {
    const shouldClear = await confirm({
      title: 'Clear all search history?',
      message: 'This cannot be undone.',
      confirmText: 'Clear All',
      danger: true,
    });
    if (!shouldClear) return;
    try {
      await api.delete('/search/history');
      setSearches([]);
      toast.success('Search history cleared');
    } catch (error) {
      console.error('Failed to clear history:', error);
      toast.error('Failed to clear search history');
    }
  };

  const rerunSearch = (search: SearchHistoryItem) => {
    const query = search.search_query?.trim() || '';
    const chain = (search.chain || 'ETH').toUpperCase();

    if (!query) return;

    if (search.search_type === 'upi' || query.includes('@')) {
      navigate(`/workbench?upi=${encodeURIComponent(query)}`);
      return;
    }

    if (search.search_type === 'transaction' || transactionService.isTransactionHash(query)) {
      navigate(`/workbench?chain=${chain}&tx=${encodeURIComponent(query)}`);
      return;
    }

    navigate(`/workbench?chain=${chain}&address=${encodeURIComponent(query)}`);
  };

  const getRiskColor = (riskScore?: number) => {
    if (!riskScore) return 'text-gray-400';
    if (riskScore >= 0.7) return 'text-red-500';
    if (riskScore >= 0.4) return 'text-yellow-500';
    return 'text-green-500';
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className={`p-6`}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Search History</h1>
        <p className="text-gray-600">Review and re-run your previous searches</p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-2 items-center justify-between">
        <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg ${filter === 'all'
            ? 'bg-primary text-primary-foreground'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          All Searches
        </button>
        <button
          onClick={() => setFilter('address')}
          className={`px-4 py-2 rounded-lg ${filter === 'address'
            ? 'bg-primary text-primary-foreground'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          Addresses
        </button>
        <button
          onClick={() => setFilter('upi')}
          className={`px-4 py-2 rounded-lg ${filter === 'upi'
            ? 'bg-primary text-primary-foreground'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          UPI
        </button>
        <button
          onClick={() => setFilter('entity')}
          className={`px-4 py-2 rounded-lg ${filter === 'entity'
            ? 'bg-primary text-primary-foreground'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          Entities
        </button>
        </div>
        {searches.length > 0 && (
          <button
            onClick={clearAllHistory}
            className="px-4 py-2 bg-red-500/10 text-red-500 hover:bg-red-500/20 rounded-lg flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Clear All History
          </button>
        )}
      </div>

      {/* Search History List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading search history...</p>
        </div>
      ) : searches.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Search className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-4 text-gray-600">No search history yet</p>
          <p className="text-sm text-gray-500">Your searches will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {searches.map((search) => (
            <div
              key={search.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => rerunSearch(search)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Search Type Badge */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {search.search_type}
                    </span>
                    {search.chain && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {search.chain.toUpperCase()}
                      </span>
                    )}
                    {search.auto_case_created && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Auto-Case Created
                      </span>
                    )}
                  </div>

                  {/* Search Query */}
                  <div className="font-mono text-sm text-gray-900 mb-2">
                    {search.search_query}
                  </div>

                  {/* Results & Entity Info */}
                  <div className="flex items-center gap-4 text-sm">
                    {search.found_entity_id ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-green-700 font-medium">
                          Found: {search.metadata?.entity_name || 'Known Entity'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">No match</span>
                      </div>
                    )}

                    {search.metadata?.risk_score !== undefined && (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">Risk:</span>
                        <span className={`font-semibold ${getRiskColor(search.metadata.risk_score)}`}>
                          {(search.metadata.risk_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    )}

                    {search.auto_case_id && (
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4 text-primary" />
                        <a
                          href={`/cases?id=${search.auto_case_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary hover:underline"
                        >
                          View Case
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timestamp & Delete */}
                <div className="flex items-center gap-3 ml-4">
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>{formatTimeAgo(search.searched_at)}</span>
                  </div>
                  <button
                    onClick={(e) => deleteSearch(search.id, e)}
                    className="p-1.5 hover:bg-red-100 rounded text-red-500 transition-colors"
                    title="Delete this search"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmDialog}
    </div>
  );
}
